import { reportPdfExtractionError } from "utils";

import { COUNTRY_CODE } from "consts";

import { extractPdfText } from "pages/app/document-scan/utils/extractPdfText";

const MIN_TEXT_LENGTH = 20;

export type PdfValidationError = "empty" | "yearMismatch" | "farmMismatch" | "documentTypeMismatch" | "countryNotSupported";

type ValidationResult = {
  isValid: boolean;
  error?: PdfValidationError;
};

type FarmIdentifiers = {
  name?: string | null;
  addressStreet?: string | null;
  country?: COUNTRY_CODE | null;
};

const KNOWN_DOCUMENT_IDENTIFIERS: Partial<Record<COUNTRY_CODE, string[]>> = {
  [COUNTRY_CODE.GERMANY]: [
    "Anlage Nutzungsnachweis",
    "Beantragte Flächen",
    "Betriebskarte",
    "Bewirtschaftungsnachweis",
    "Feldstückliste",
    "Feldstücksliste",
    "Flächen- und Nutzungsnachweis",
    "Flächenaufstellung",
    "Flächennachweis",
    "Flächenverzeichnis",
    "Flächenverzeichnis zum Sammelantrag",
    "FNN",
    "Geografischer Flächennachweis",
    "Geographischer Flächennachweis",
    "Gesamtparzellenliste",
    "Grundinformationen zum Schlag",
    "Hauptnutzungen",
    "Informationen zu LE-Teilschlägen",
    "Informationen zu Teilschlägen",
    "Karte der beantragten Flächen",
    "Kurzfassung des Flächen- und Nutzungsnachweis",
    "Kurzfassung des Flächen- und Nutzungsnachweises",
    "Landschaftselementnachweis",
    "LE-Verzeichnis",
    "Nutzflächenaufstellung",
    "Parzellenliste",
    "Parzellenübersicht",
    "Schlag- und Nutzungsübersicht",
    "Schlageinstufung und Flächen für Direktzahlungen",
    "Flächen für Direktzahlungen",
    "Schlagliste",
    "Schlagübersicht",
    "Schlägeübersicht",
    "Export ausgewählter Schläge",
    // Column identifiers to handle edge cases of no document name
    "Lage-Bezeichnung",
    "Code für Nutzung",
    "Nutzungs-Code",
  ],
  [COUNTRY_CODE.POLAND]: [
    "W-1/01",
    "Agencja Restrukturyzacji i Modernizacji Rolnictwa",
    "WNIOSEK O PRZYZNANIE PŁATNOŚCI",
    "Numer identyfikacyjny",
    "CEL ZŁOŻENIA",
  ],
};

const MIN_WORD_LENGTH = 3;
const MIN_PREFIX_LENGTH = 4;

/**
 * Checks whether a word from the identifier matches any word in the text.
 * Supports prefix matching to handle abbreviations (e.g. "Agrarbewirt." matches "Agrarbewirtschaftung").
 * @param identifierWord - A single word from the farm identifier.
 * @param textWords - All words from the extracted text.
 * @returns `true` if the word matches any text word exactly or by prefix.
 */
const wordMatchesAny = (identifierWord: string, textWords: string[]): boolean => {
  const stripped = identifierWord.replace(/[.]$/, "");

  return textWords.some((textWord) => {
    if (stripped === textWord) return true;
    if (stripped.length >= MIN_PREFIX_LENGTH && textWord.startsWith(stripped)) return true;
    if (textWord.length >= MIN_PREFIX_LENGTH && stripped.startsWith(textWord)) return true;
    return false;
  });
};

/**
 * Checks whether any of the farm's identifiers (name, street) appear in the extracted text.
 * Uses word-level matching with prefix support to handle abbreviations in document text.
 * Requires at least half of the identifier's words (min 1) to match.
 * @param text - The extracted document text.
 * @param farm - The farm identifiers to match against.
 * @returns `true` if at least one identifier is found in the text.
 */
const matchesFarm = (text: string, farm: FarmIdentifiers): boolean => {
  const textWords = text.toLowerCase().split(/\s+/);
  const identifiers = [farm.name, farm.addressStreet].filter((v): v is string => !!v && v.trim().length > 0);

  return identifiers.some((identifier) => {
    const words = identifier
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.replace(/[.]$/, "").length >= MIN_WORD_LENGTH);

    if (words.length === 0) return false;

    const matchedCount = words.filter((word) => wordMatchesAny(word, textWords)).length;
    return matchedCount >= Math.ceil(words.length / 2);
  });
};

/**
 * Validates an uploaded PDF against expected content: extracts text (via embedded text layer or OCR),
 * then checks for farm identity, recognized document type, and the selected reporting year.
 *
 * A PDF that cannot be read passes unchecked, so a technical failure never blocks the upload.
 *
 * Validation order: empty → farmMismatch → documentTypeMismatch / countryNotSupported → yearMismatch → valid.
 *
 * @param file - The uploaded PDF file.
 * @param selectedYear - The reporting year expected to appear in the document.
 * @param farm - Optional farm identifiers and country used for farm and document type checks.
 */
export const validatePdfContent = async (file: File, selectedYear: number, farm?: FarmIdentifiers): Promise<ValidationResult> => {
  let extractedText: string;

  try {
    extractedText = await extractPdfText(file);
  } catch (error) {
    reportPdfExtractionError(error);
    return { isValid: true };
  }

  if (extractedText.length < MIN_TEXT_LENGTH) {
    return { isValid: false, error: "empty" };
  }

  if (!farm || !matchesFarm(extractedText, farm)) {
    return { isValid: false, error: "farmMismatch" };
  }

  if (!farm?.country) {
    return { isValid: false, error: "documentTypeMismatch" };
  }

  const documentNames = KNOWN_DOCUMENT_IDENTIFIERS[farm.country ?? COUNTRY_CODE.GERMANY];

  if (!documentNames) {
    return { isValid: false, error: "countryNotSupported" };
  }

  const lowerText = extractedText.toLowerCase();
  const documentName = documentNames.find((name) => lowerText.includes(name.toLowerCase()));

  if (!documentName) {
    return { isValid: false, error: "documentTypeMismatch" };
  }

  if (!extractedText.includes(String(selectedYear))) {
    return { isValid: false, error: "yearMismatch" };
  }

  return { isValid: true };
};
